/**
 * Product Card Swatch Image Switcher
 * Handles updating product card images when color swatches are clicked
 */

class ProductCardSwatch extends HTMLElement {
  constructor() {
    super();
    this.productCard = this.closest('.product-card');
    if (!this.productCard) return;

    this.imageWrapper = this.productCard.querySelector('.product-card__image-wrapper');
    this.mainImage = this.productCard.querySelector('.product-card__image--main img');
    this.secondImage = this.productCard.querySelector('.product-card__image--second img');
    this.swatches = this.querySelectorAll('.swatch-color');

    this.originalMainImageSrc = this.mainImage?.currentSrc || this.mainImage?.src;
    this.originalSecondImageSrc = this.secondImage?.currentSrc || this.secondImage?.src;

    this.init();
  }

  init() {
    if (!this.mainImage || this.swatches.length === 0) return;

    this.swatches.forEach((swatch) => {
      swatch.addEventListener('click', this.handleSwatchClick.bind(this));
      swatch.addEventListener('mouseenter', this.handleSwatchHover.bind(this));
    });

  }

  handleSwatchClick(event) {
    event.preventDefault();
    const swatch = event.currentTarget;
    const variantData = this.getVariantData(swatch);

    if (!variantData) return;

    this.updateActiveState(swatch);

    this.updateProductImage(variantData);

    this.updateProductUrl(variantData);
  }

  handleSwatchHover(event) {
    const swatch = event.currentTarget;
    const variantData = this.getVariantData(swatch);

    if (!variantData) return;

    this.updateProductImage(variantData);
  }

  getVariantData(swatch) {
    const variantIndex = swatch.dataset.index;
    const variantId = swatch.dataset.variantId;
    const variantImage = swatch.dataset.variantImage;
    const variantSecondImage = swatch.dataset.variantSecondImage;
    const variantUrl = swatch.dataset.variantUrl || swatch.href;

    if (!variantImage) return null;

    return {
      index: variantIndex,
      id: variantId,
      image: variantImage,
      secondImage: variantSecondImage,
      url: variantUrl
    };
  }

  updateActiveState(clickedSwatch) {
    this.swatches.forEach(swatch => swatch.classList.remove('active'));
    clickedSwatch.classList.add('active');
  }

  updateProductImage(variantData) {
    if (!this.mainImage || !variantData.image) return;
    this.mainImage.parentElement.style.opacity = '0';
    this.secondImage.parentElement.style.opacity = '0';

    if (variantData.index == 1) {
      setTimeout(() => {
        const imageWidth = this.mainImage.naturalWidth || 900;
        this.mainImage.src = this.getImageUrl(variantData.image, imageWidth);

        if (this.mainImage.srcset) {
          this.mainImage.srcset = this.generateSrcset(variantData.image);
        }

        const altText = this.mainImage.alt.split(' - ')[0];
        this.mainImage.alt = `${altText} - ${variantData.url?.split('variant=')[1] || ''}`;

        this.mainImage.parentElement.style.opacity = '1';
      }, 150);
    } else {
      if (this.secondImage && variantData.secondImage) {

        setTimeout(() => {
          const imageWidth = this.secondImage.naturalWidth || 900;
          this.secondImage.src = this.getImageUrl(variantData.secondImage, imageWidth);

          if (this.secondImage.srcset) {
            this.secondImage.srcset = this.generateSrcset(variantData.secondImage);
          }

          this.secondImage.parentElement.style.opacity = '1';
        }, 150);
      }
  }
  }

  resetToOriginalImage() {
    if (!this.mainImage) return;

    this.mainImage.style.opacity = '0';

    setTimeout(() => {
      this.mainImage.src = this.originalMainImageSrc;
      this.mainImage.style.opacity = '1';
    }, 150);

    if (this.secondImage && this.originalSecondImageSrc) {
      this.secondImage.style.opacity = '0';

      setTimeout(() => {
        this.secondImage.src = this.originalSecondImageSrc;
        this.secondImage.style.opacity = '1';
      }, 150);
    }
  }

  updateProductUrl(variantData) {
    if (!variantData.url) return;

    const productLink = this.productCard.querySelector('a[href*="/products/"]');
    if (productLink) {
      productLink.href = variantData.url;
    }
  }

  getImageUrl(imageUrl, width) {
    const urlParts = imageUrl.split('?')[0];
    return `${urlParts}?width=${width}`;
  }

  generateSrcset(imageUrl) {
    const widths = [165, 360, 535, 750, 900, 1100, 1500];
    const baseUrl = imageUrl.split('?')[0];

    return widths
      .map(width => `${baseUrl}?width=${width} ${width}w`)
      .join(', ');
  }
}

if (!customElements.get('product-card-swatch')) {
  customElements.define('product-card-swatch', ProductCardSwatch);
}

